
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, ExternalLink, Trash2, Download } from "lucide-react";
import { UserWithProfile } from "@/types/admin";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface NewsletterSubscription {
  id: string;
  email: string;
  subscribed_at: string;
  is_active: boolean;
  user_id: string | null;
}

interface UsersManagementSectionProps {
  users: UserWithProfile[];
  newsletterSubscriptions: NewsletterSubscription[];
  loading: boolean;
}

export const UsersManagementSection = ({ users, newsletterSubscriptions, loading }: UsersManagementSectionProps) => {
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const handleToggleSubscription = async (userId: string, userEmail: string, currentlySubscribed: boolean) => {
    setTogglingUserId(userId);
    try {
      if (currentlySubscribed) {
        // Unsubscribe: update existing subscription to inactive
        const { error } = await supabase
          .from('newsletter_subscriptions')
          .update({ is_active: false })
          .eq('email', userEmail.toLowerCase());
        
        if (error) throw error;
        toast.success(`${userEmail} has been unsubscribed`);
      } else {
        // Subscribe: upsert a subscription record
        const { error } = await supabase
          .from('newsletter_subscriptions')
          .upsert({ 
            email: userEmail.toLowerCase(), 
            is_active: true,
            user_id: userId 
          }, { onConflict: 'email' });
        
        if (error) throw error;
        toast.success(`${userEmail} has been subscribed`);
      }
    } catch (error: any) {
      console.error("Error toggling subscription:", error);
      toast.error(`Failed to update subscription: ${error?.message || 'Unknown error'}`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const formatUserName = (profile: UserWithProfile["profile"]) => {
    if (!profile) return "No Profile";
    
    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || "Unknown User";
  };

  const calculateAge = (dateOfBirth: string | null | undefined): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Create a map of user emails to their subscription status
  const subscriptionMap = new Map();
  newsletterSubscriptions.forEach(sub => {
    subscriptionMap.set(sub.email.toLowerCase(), sub);
  });

  // Get standalone newsletter subscriptions (not linked to any registered user)
  const standaloneSubscriptions = newsletterSubscriptions.filter(sub => 
    sub.is_active && !users.some(user => user.email.toLowerCase() === sub.email.toLowerCase())
  );

  // Limit to first 5 users and first 3 newsletter-only subscribers
  const displayedUsers = users.slice(0, 5);
  const displayedStandaloneSubscriptions = standaloneSubscriptions.slice(0, 3);

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    setDeletingUserId(userId);
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          targetUserId: userId,
          userEmail: userEmail
        }
      });
      
      if (error) {
        console.error("Error deleting user:", error);
        toast.error(`Failed to delete user: ${error.message}`);
        return;
      }

      if (data?.error) {
        console.error("Server error deleting user:", data.error);
        toast.error(`Failed to delete user: ${data.error}`);
        return;
      }

      toast.success(`User ${userEmail} has been deleted successfully`);
      // The useAdminData hook should automatically refresh the data
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error(`Failed to delete user: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  const exportNewsletterSubscriptions = () => {
    const activeSubscriptions = newsletterSubscriptions.filter(sub => sub.is_active);
    const emailList = activeSubscriptions.map(sub => sub.email).join('\n');
    
    const blob = new Blob([emailList], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscriptions-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Newsletter subscriptions exported successfully');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-poppins text-2xl">Users Overview</CardTitle>
            <CardDescription className="font-inter">
              Recent registered users and newsletter subscriptions
            </CardDescription>
          </div>
          <Link to="/admin/users">
            <Button variant="outline" className="flex items-center gap-2">
              View All <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Registered Users Table */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-poppins text-lg font-semibold">
                  Recent Users ({displayedUsers.length} of {users.length})
                </h3>
                {users.length > 5 && (
                  <Link to="/admin/users">
                    <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800">
                      View all {users.length} users
                    </Button>
                  </Link>
                )}
              </div>
              {displayedUsers.length === 0 ? (
                <p className="text-center py-8 text-gray-500 font-inter">
                  No registered users found.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-poppins">Name</TableHead>
                      <TableHead className="font-poppins">Email</TableHead>
                      <TableHead className="font-poppins">Age</TableHead>
                      <TableHead className="font-poppins">Phone</TableHead>
                      <TableHead className="font-poppins">School</TableHead>
                      <TableHead className="font-poppins">Joined</TableHead>
                      <TableHead className="font-poppins">Newsletter</TableHead>
                      <TableHead className="font-poppins">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedUsers.map((user) => {
                      const subscription = subscriptionMap.get(user.email.toLowerCase());
                      const age = calculateAge(user.profile?.date_of_birth);
                      return (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium font-inter">
                            {formatUserName(user.profile)}
                          </TableCell>
                          <TableCell className="font-inter">
                            {user.email}
                          </TableCell>
                          <TableCell className="font-inter text-sm">
                            {age !== null ? age : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="font-inter text-sm">
                            {user.profile?.phone_number || <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="font-inter text-sm">
                            {user.profile?.school || <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="font-inter">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const isSubscribed = subscription?.is_active !== false;
                              return (
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={isSubscribed}
                                    onCheckedChange={() => handleToggleSubscription(user.id, user.email, isSubscribed)}
                                    disabled={togglingUserId === user.id}
                                  />
                                  <span className={`text-sm font-medium ${isSubscribed ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                                  </span>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  disabled={deletingUserId === user.id}
                                >
                                  {deletingUserId === user.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete User</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete user "{user.email}"? This action cannot be undone and will permanently remove all their data.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(user.id, user.email)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete User
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Newsletter-Only Subscribers */}
            {displayedStandaloneSubscriptions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-poppins text-lg font-semibold">
                    Newsletter-Only Subscribers ({displayedStandaloneSubscriptions.length} of {standaloneSubscriptions.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button onClick={exportNewsletterSubscriptions} variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      Export All
                    </Button>
                    {standaloneSubscriptions.length > 3 && (
                      <Link to="/admin/users">
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800">
                          View all {standaloneSubscriptions.length} subscriptions
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-poppins">Email</TableHead>
                      <TableHead className="font-poppins">Subscribed Date</TableHead>
                      <TableHead className="font-poppins">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedStandaloneSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium font-inter">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            {subscription.email}
                          </div>
                        </TableCell>
                        <TableCell className="font-inter">
                          {new Date(subscription.subscribed_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                            Newsletter Only
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
